import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import hostelService from '../../services/hostelService';
import DialogModal from '../../components/DialogModal';
import { Plus, MagnifyingGlass, PencilSimple, Trash, CircleNotch, ArrowLeft } from '@phosphor-icons/react';
import './hostel.css';

function HostelsManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hostels, setHostels] = useState([]);
  const [filteredHostels, setFilteredHostels] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    hostel_name: '',
    hostel_code: '',
    hostel_type: 'boys',
    capacity: 0,
    warden_name: '',
    warden_phone: '',
    warden_email: '',
    location: '',
    address: '',
    has_wifi: false,
    has_laundry: false,
    has_kitchen: false,
    has_common_room: false,
    has_security: false,
    notes: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, hostelId: null, message: '', loading: false });

  useEffect(() => {
    loadHostels();
  }, []);

  useEffect(() => {
    filterHostels();
  }, [hostels, searchTerm, statusFilter, filterHostels]);

  const loadHostels = async () => {
    try {
      setLoading(true);
      const data = await hostelService.listHostels();
      const hostelList = Array.isArray(data) ? data : data.hostels || [];
      setHostels(hostelList);
    } catch (error) {
      console.error('Error loading hostels:', error);
      toast.error('Failed to load hostels');
    } finally {
      setLoading(false);
    }
  };

  const filterHostels = React.useCallback(() => {
    let filtered = hostels;

    if (searchTerm) {
      filtered = filtered.filter(h =>
        h.hostel_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        h.hostel_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        h.warden_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredHostels(filtered);
  }, [hostels, searchTerm, statusFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await hostelService.updateHostel(editingId, formData);
        toast.success('Hostel updated successfully');
      } else {
        await hostelService.createHostel(formData);
        toast.success('Hostel created successfully');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({
        hostel_name: '',
        hostel_code: '',
        hostel_type: 'boys',
        capacity: 0,
        warden_name: '',
        warden_phone: '',
        warden_email: '',
        location: '',
        address: '',
        has_wifi: false,
        has_laundry: false,
        has_kitchen: false,
        has_common_room: false,
        has_security: false,
        notes: ''
      });
      loadHostels();
    } catch (error) {
      console.error('Error saving hostel:', error);
      toast.error(editingId ? 'Failed to update hostel' : 'Failed to create hostel');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (hostel) => {
    setFormData(hostel);
    setEditingId(hostel.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setDeleteDialog({ isOpen: true, hostelId: id, message: 'Checking what this will affect...', loading: true });
    try {
      const impact = await hostelService.getHostelDeleteImpact(id);
      let message;
      if (impact.total_to_be_deleted > 0) {
        const deletedLines = Object.entries(impact.will_be_deleted)
          .map(([label, count]) => `- ${count} ${label}`)
          .join('\n');
        message = `This will permanently delete ${impact.total_to_be_deleted} record(s):\n${deletedLines}\n\nThis cannot be undone.`;
      } else {
        message = 'No rooms, allocations, or other records are tied to this hostel yet — safe to delete.';
      }
      if (Object.keys(impact.will_be_preserved || {}).length > 0) {
        const preservedLines = Object.entries(impact.will_be_preserved)
          .map(([label, count]) => `- ${count} ${label}`)
          .join('\n');
        message += `\n\nThese will be kept, just unlinked from this hostel:\n${preservedLines}`;
      }
      setDeleteDialog({ isOpen: true, hostelId: id, message, loading: false });
    } catch (error) {
      console.error('Error fetching delete impact:', error);
      setDeleteDialog({ isOpen: true, hostelId: id, message: 'Delete this hostel? This cannot be undone.', loading: false });
    }
  };

  const confirmDeleteHostel = async () => {
    const id = deleteDialog.hostelId;
    setDeleteDialog({ isOpen: false, hostelId: null, message: '', loading: false });
    try {
      await hostelService.deleteHostel(id);
      toast.success('Hostel deleted successfully');
      loadHostels();
    } catch (error) {
      console.error('Error deleting hostel:', error);
      toast.error('Failed to delete hostel');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      hostel_name: '',
      hostel_code: '',
      hostel_type: 'boys',
      capacity: 0,
      warden_name: '',
      warden_phone: '',
      warden_email: '',
      location: '',
      address: '',
      has_wifi: false,
      has_laundry: false,
      has_kitchen: false,
      has_common_room: false,
      has_security: false,
      notes: ''
    });
  };

  if (loading) {
    return <Layout><div className="loading">Loading hostels...</div></Layout>;
  }

  return (
    <Layout>
      <div className="page hostel-page">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/hostel')} title="Back to Hostel">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Hostels Management</h1>
              <p>Create and manage residential hostels</p>
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
            }}
          >
            <Plus size={20} />
            Add Hostel
          </button>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search hostels..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {showForm && (
          <div className="form-container">
            <div className="form-card">
              <h2>{editingId ? 'Edit Hostel' : 'Add New Hostel'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Hostel Name *</label>
                  <input
                    type="text"
                    value={formData.hostel_name}
                    onChange={(e) => setFormData({ ...formData, hostel_name: e.target.value })}
                    required
                    disabled={submitting}
                    placeholder="e.g., Boys Hostel A"
                  />
                </div>

                <div className="form-group">
                  <label>Hostel Code *</label>
                  <input
                    type="text"
                    value={formData.hostel_code}
                    onChange={(e) => setFormData({ ...formData, hostel_code: e.target.value })}
                    required
                    disabled={submitting}
                    placeholder="e.g., BH-001"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Type *</label>
                    <select
                      value={formData.hostel_type}
                      onChange={(e) => setFormData({ ...formData, hostel_type: e.target.value })}
                      required
                      disabled={submitting}
                    >
                      <option value="boys">Boys</option>
                      <option value="girls">Girls</option>
                      <option value="mixed">Mixed</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Capacity *</label>
                    <input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                      required
                      disabled={submitting}
                      placeholder="e.g., 100"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    disabled={submitting}
                    placeholder="Hostel location"
                  />
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    disabled={submitting}
                    placeholder="Full address"
                  />
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    disabled={submitting}
                    placeholder="Additional notes..."
                    rows={3}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Warden Name</label>
                    <input
                      type="text"
                      value={formData.warden_name}
                      onChange={(e) => setFormData({ ...formData, warden_name: e.target.value })}
                      disabled={submitting}
                      placeholder="Warden full name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Warden Phone</label>
                    <input
                      type="tel"
                      value={formData.warden_phone}
                      onChange={(e) => setFormData({ ...formData, warden_phone: e.target.value })}
                      disabled={submitting}
                      placeholder="Phone number"
                    />
                  </div>

                  <div className="form-group">
                    <label>Warden Email</label>
                    <input
                      type="email"
                      value={formData.warden_email}
                      onChange={(e) => setFormData({ ...formData, warden_email: e.target.value })}
                      disabled={submitting}
                      placeholder="Email address"
                    />
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.has_wifi}
                      onChange={(e) => setFormData({ ...formData, has_wifi: e.target.checked })}
                      disabled={submitting}
                    />
                    Has WiFi
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.has_laundry}
                      onChange={(e) => setFormData({ ...formData, has_laundry: e.target.checked })}
                      disabled={submitting}
                    />
                    Has Laundry
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.has_kitchen}
                      onChange={(e) => setFormData({ ...formData, has_kitchen: e.target.checked })}
                      disabled={submitting}
                    />
                    Has Kitchen
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.has_common_room}
                      onChange={(e) => setFormData({ ...formData, has_common_room: e.target.checked })}
                      disabled={submitting}
                    />
                    Has Common Room
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.has_security}
                      onChange={(e) => setFormData({ ...formData, has_security: e.target.checked })}
                      disabled={submitting}
                    />
                    Has Security
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Saving...
                      </span>
                    ) : editingId ? 'Update Hostel' : 'Create Hostel'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={submitting}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="table-container">
          {filteredHostels.length === 0 ? (
            <div className="empty-state">
              <p>No hostels found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Capacity</th>
                  <th>Warden</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredHostels.map((hostel) => (
                  <tr key={hostel.id}>
                    <td>{hostel.hostel_name}</td>
                    <td className="text-capitalize">{hostel.hostel_type}</td>
                    <td>{hostel.capacity}</td>
                    <td>{hostel.warden_name || '-'}</td>
                    <td>
                      <span className={`badge badge-${hostel.status === 'active' ? 'success' : 'secondary'}`}>
                        {hostel.status || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          className="btn-icon"
                          onClick={() => handleEdit(hostel)}
                          title="Edit"
                        >
                          <PencilSimple size={18} />
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleDelete(hostel.id)}
                          title="Delete"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <DialogModal
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, hostelId: null, message: '', loading: false })}
        title="Delete Hostel"
        message={deleteDialog.message}
        type="warning"
        isLoading={deleteDialog.loading}
        disableCloseWhenLoading={false}
        primaryAction={deleteDialog.loading ? null : { label: 'Delete', onClick: confirmDeleteHostel }}
        secondaryAction={deleteDialog.loading ? null : { label: 'Cancel', onClick: () => {} }}
      />
    </Layout>
  );
}

export default HostelsManagementPage;
